import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {Slide} from 'react-slideshow-image';
import FullScreenView from 'components/FullScreenView';
import {AppContext} from 'AppContext';
import configuration from 'configuration';

const SlideScreen = ({path, items}: {path: string; items: number}) => {
  const {t} = useTranslation();
  const [page, setPage] = useState(items > 1 ? 1 : 0);
  const {setCustomNavElement} = useContext(AppContext);

  const slides = useMemo(() => {
    const pathPrefix = configuration.assetsPath(path) + 'screen';
    const result: string[] = [];
    for (let i = 1; i <= items; i++) {
      result.push(pathPrefix + String(i).padStart(2, '0') + '.jpg');
    }
    return result;
  }, [items, path]);

  const texts = useMemo(() => {
    const cfg: string[] = t(configuration.path2langKey(path, 'texts'), {
      returnObjects: true,
      defaultValue: [],
    });
    return cfg;
  }, [path, t]);

  const imagePositions = useMemo(() => {
    const def: string | null = t(
      [
        configuration.path2langKey(path, '$images.default.position'),
        configuration.path2langKey(path, '$images.position'),
      ],
      {defaultValue: null, fallbackLng: 'de'},
    );
    const result: Array<string | undefined> = [];
    for (let i = 1; i <= items; i++) {
      result.push(
        t(configuration.path2langKey(path, '$images.' + String(i) + '.position'), {
          defaultValue: def,
          fallbackLng: 'de',
        }) ?? undefined,
      );
    }
    return result;
  }, [items, path, t]);

  const className = useMemo(() => {
    const cn = 'slide-screen page-' + String(page);
    return page === items ? cn + ' page-last' : cn;
  }, [items, page]);

  useEffect(() => {
    const pageCnt = page > 0 && items > 0 ? `[${page}/${items}]` : null;
    setCustomNavElement(pageCnt && <PageNumbers className="no-select">{pageCnt}</PageNumbers>);
    return () => setCustomNavElement(null);
  }, [setCustomNavElement, items, page]);

  return (
    <div className={className}>
      <Slide
        autoplay={false}
        canSwipe={false}
        transitionDuration={500}
        easing="cubic"
        onChange={(_, to) => setPage(to + 1)}>
        {slides.map((url, index) => (
          <SlideView key={index} image={url} imagePosition={imagePositions[index]}>
            {texts[index] && (
              <Text className="no-select">{texts[index].replaceAll('\\n', '\n')}</Text>
            )}
          </SlideView>
        ))}
      </Slide>
    </div>
  );
};
export default SlideScreen;

const SlideView = styled(FullScreenView)`
  height: calc(100vh - var(--nav-bar-height) - 1px);
`;

const Text = styled.div`
  width: 300px;
  min-width: 300px;
  padding: 50px;
  text-align: center;
  white-space: pre-line;
  font-size: 50px;
  font-weight: bold;
  background-color: var(--color-alpha-white);
`;

const PageNumbers = styled.div`
  width: 60px;
  font-size: 14px;
  text-align: center;
`;
